import { Card, EmptyView, ErrorView, InfoPanel, LoadingView, StatusBadge, DashStyles as Styles, TableWrap } from '../../components/dashboard/DashboardUI';
import { FeedbackMessage } from '../../components/dashboard/FeedbackMessage';
import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useLobbySettings } from '../../providers/LobbySettingsProvider';
import { Button, Label } from '@app/engine';
import { CommonConstants, FormatLocalized, FormatTimestamp, Logger } from '@shared/common';
import { KBClient, Telemetry } from '@shared/frontend';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { IFeedbackDTO } from '../../components/dashboard/FeedbackMessage';
import type { DragEvent } from 'react';

/** File-local constants. */
const localConstants = {
  logContext: 'Knowledge',
  // The file picker's accept attribute and the client-side validation whitelist. Kept in sync with the billing service's KB ingest guard (`SupportedContentTypes` / `MaxUploadBytes`); the server
  // re-validates, so this is a fast fail for the operator, not the security boundary.
  fileAccept: '.txt,.pdf,text/plain,application/pdf',
  acceptedExtensions: ['.txt', '.pdf'] as string[],
  acceptedTypes: ['text/plain', 'application/pdf'] as string[],
  maxUploadBytes: 10 * 1024 * 1024
} as const;

/**
 * Static "How your knowledge base works" guidance shown beside the document list, mirroring the help panel on the Account page. Explains accepted file types, the formats the assistant reads
 * best, how to organize content, and how documents are used at call time.
 *
 * @returns The knowledge help panel element.
 */
function KnowledgeHelpPanel() {
  const { contactEmail } = useLobbySettings();
  return (
    <InfoPanel title={Localized.KnowledgeHelpTitle}>
      <Label classNameContainer={Styles.muted} text={Localized.KnowledgeHelpIntro} />
      <Label classNameContainer={Styles.muted} text={Localized.KnowledgeHelpFormats} />
      <Label classNameContainer={Styles.muted} text={Localized.KnowledgeHelpManage} />
      <Label classNameContainer={Styles.muted} text={Localized.KnowledgeHelpOrganize} />
      <Label classNameContainer={Styles.muted} text={Localized.KnowledgeHelpUsage} />
      <Label classNameContainer={Styles.muted} text={Localized.KnowledgeHelpHours} />
      <Label classNameContainer={Styles.muted} text={FormatLocalized(Localized.AccountHelpContact, contactEmail)} />
    </InfoPanel>
  );
}

/**
 * Knowledge base screen: list, upload, and delete documents.
 *
 * @returns The knowledge screen element.
 */
export function Knowledge() {
  const { tenantID } = useDashboard();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useDashboardData((signal) => KBClient.ListDocuments(signal, tenantID!), [tenantID]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [busyID, setBusyID] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<IFeedbackDTO | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // A file is accepted only when its extension is on the whitelist, its MIME type is allowed (or blank - some browsers report no type for .txt), and it is within the size ceiling. The server
  // re-validates on ingest, so this only spares the operator a doomed round-trip.
  const isAcceptedFile = (file: File): boolean => {
    const name = file.name.toLowerCase();
    const extensionOk = localConstants.acceptedExtensions.some((ext) => name.endsWith(ext));
    const typeOk = file.type.length === 0 || localConstants.acceptedTypes.includes(file.type);
    return extensionOk && typeOk && file.size <= localConstants.maxUploadBytes;
  };

  // Uploads one or more files sequentially (multi-select and drag-drop both funnel here). Invalid files are dropped up front; a mixed batch still uploads the valid ones and reports the skips.
  const onUploadFiles = async (files: File[]) => {
    const valid = files.filter(isAcceptedFile);
    const skipped = files.length - valid.length;
    if (valid.length === 0) {
      setFeedback({ text: Localized.KnowledgeUploadInvalid, type: 'error' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    // Hard per-tenant document cap (server-enforced too): reject the batch up front if it would push the tenant over the limit, telling them the exact number.
    if ((data?.length ?? 0) + valid.length > CommonConstants.Validation.KnowledgeDocumentsMaxCount) {
      setFeedback({ text: FormatLocalized(Localized.KnowledgeLimitReached, CommonConstants.Validation.KnowledgeDocumentsMaxCount), type: 'error' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(true);
    setFeedback(null);
    let failed = 0;
    for (const file of valid) {
      try {
        const controller = new AbortController();
        await KBClient.UploadDocument(controller.signal, tenantID!, file, file.name);
        Telemetry.KnowledgeDocumentUploaded(file.type, file.size);
      } catch (err: unknown) {
        failed++;
        Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    reload();
    if (failed > 0) setFeedback({ text: Localized.KnowledgeUploadError, type: 'error' });
    else if (skipped > 0) setFeedback({ text: Localized.KnowledgeUploadSomeSkipped, type: 'error' });
  };

  // Drag-drop onto the Documents panel: highlight while a drag hovers, and upload the dropped files (validated in onUploadFiles) on release.
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) void onUploadFiles(files);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!dragActive) setDragActive(true);
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
  };

  const onDelete = async (documentID: string) => {
    if (!window.confirm(Localized.KnowledgeDeleteConfirm)) return;
    setBusyID(documentID);
    setFeedback(null);
    try {
      const controller = new AbortController();
      await KBClient.DeleteDocument(controller.signal, tenantID!, documentID);
      Telemetry.KnowledgeDocumentDeleted();
      reload();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setFeedback({ text: Localized.KnowledgeDeleteError, type: 'error' });
    } finally {
      setBusyID(null);
    }
  };

  // Opens the in-app editor for a document. Every document body is editable plain text.
  const onEdit = (documentID: string) => {
    setFeedback(null);
    navigate(Constants.Route.KnowledgeEdit.replace(`:${Constants.Param.DocumentID}`, documentID));
  };

  const uploadAction = (
    // headerActions keeps the two buttons grouped together on the header's right; as loose fragment children of the space-between header, Refresh floated to the center.
    <div className={Styles.headerActions}>
      {/* Raw <input>: hidden file picker needing a ref, type=file, multiple, accept, and event.target.files; engine Textbox is a text input supporting none of these. */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={localConstants.fileAccept}
        style={{ display: 'none' }}
        onChange={(event) => {
          const files = event.target.files ? Array.from(event.target.files) : [];
          if (files.length > 0) void onUploadFiles(files);
        }}
      />
      {/* Refresh re-reads the document list so ingest Status updates land without a page reload or another upload. */}
      <Button
        text={Localized.SharedRefresh}
        disabled={loading}
        classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
        classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
        onClickHandler={() => {
          setFeedback(null);
          reload();
        }}
      />
      <Button
        text={uploading ? Localized.KnowledgeUploading : Localized.KnowledgeUpload}
        disabled={uploading}
        classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
        classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
        onClickHandler={() => fileInputRef.current?.click()}
      />
      <Button
        text={Localized.KnowledgeCreate}
        classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
        classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
        onClickHandler={() => {
          // Same hard cap as uploads: block a new manual document at the limit and tell the owner the number instead of routing to an editor that would fail on save.
          if ((data?.length ?? 0) >= CommonConstants.Validation.KnowledgeDocumentsMaxCount) {
            setFeedback({ text: FormatLocalized(Localized.KnowledgeLimitReached, CommonConstants.Validation.KnowledgeDocumentsMaxCount), type: 'error' });
            return;
          }
          navigate(Constants.Route.KnowledgeNew);
        }}
      />
    </div>
  );

  const knowledgeCard = (
    <div className={dragActive ? `${Styles.dropZone} ${Styles.dropZoneActive}` : Styles.dropZone} onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}>
      <Card title={Localized.KnowledgeDocuments} actions={uploadAction} feedback={<FeedbackMessage feedback={feedback} />}>
        {loading ? (
          <LoadingView className={Styles.panelLoading} />
        ) : error ? (
          <ErrorView onRetry={reload} />
        ) : (data ?? []).length === 0 ? (
          <EmptyView message={Localized.KnowledgeNone} />
        ) : (
          <TableWrap>
            <table className={Styles.table}>
              <thead>
                <tr>
                  <th>
                    <Label text={Localized.KnowledgeDocTitle} />
                  </th>
                  <th>
                    <Label text={Localized.SharedStatus} />
                  </th>
                  <th>
                    <Label text={Localized.SharedUpdated} />
                  </th>
                  <th>
                    <Label text={Localized.SharedActions} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      {/* Every document body is editable plain text, so the title opens the in-app editor. */}
                      <Label classNameContainer={Styles.tableLink} text={doc.title} title={Localized.KnowledgeEditHint} onClickHandler={() => onEdit(doc.id)} />
                    </td>
                    <td>
                      <StatusBadge status={doc.status} />
                    </td>
                    <td>
                      <Label text={FormatTimestamp(doc.updatedAt ?? doc.createdAt)} />
                    </td>
                    <td>
                      <Button
                        text={Localized.KnowledgeDelete}
                        disabled={busyID === doc.id}
                        classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.btnDanger}`}
                        classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.btnDanger}`}
                        onClickHandler={() => void onDelete(doc.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>
    </div>
  );

  // Both panels are wide-format (help prose, six-column table), so each takes its own full row instead of the two-up .cardRow split used elsewhere.
  return (
    <div className={Styles.cardStack}>
      <KnowledgeHelpPanel />
      {knowledgeCard}
    </div>
  );
}
